import Vue from "vue";
import { Route } from "vue-router";

import { router } from "@/@core";

import { Modal } from "./types";

import BaseModal from "./components/BaseModal.vue";

Vue.component("base-modal", BaseModal);

let modalContainer: Vue & {
  modals: Modal[];
} = null as any;

export const modal = {
  show<T>(componentType: typeof Vue, props: any = {}): Promise<T> {
    const modal = { componentType: componentType, props } as Modal;
    const count = modalContainer.modals.push(modal);

    // initial query pushed in router
    let query = { ...router.currentRoute.query, m: count.toString() };

    // check if isSameQueryM:
    //   if query.m already exists
    //   && if current value of query.m == count
    const isSameQueryM = !!router.currentRoute.query.m && router.currentRoute.query.m === count.toString();

    // if isSameQueryM:
    //   increment count to avoid createRouterError/createNavigationDuplicatedError
    //   error: "Avoided redundant navigation"
    if (isSameQueryM) {
      query = { ...router.currentRoute.query, m: (count + 1).toString() };
    }

    router.push({ query: query });

    return new Promise((resolve, reject) => {
      modal.resolve = resolve;
      modal.reject = reject;
    });
  },
  close(data: any = null) {
    modalContainer.$emit("modal-close", data);
  },
  dismiss(reason = null) {
    modalContainer.$emit("modal:dismiss", { reason });
  },
  onDismiss(handler: () => Promise<boolean>) {
    modalContainer.modals[modalContainer.modals.length - 1].dismissHandler = handler;
  },
};

// called during app bootstrapping from `mounted` in `ModalContainer.vue`
export function initContainer(modalContainerInstance: Vue & { modals: Modal[] }) {
  modalContainer = modalContainerInstance;

  modalContainer.$on("modal-close", (data: any) => {
    modalContainer.modals.pop()!.resolve!(data);
    router.go(-1);
  });

  modalContainer.$on("modal:dismiss", async (err: any) => {
    const handler = modalContainer.modals[modalContainer.modals.length - 1].dismissHandler;
    if (handler) {
      const cancel = await handler();
      if (cancel) {
        return;
      }
    }

    modalContainer.modals.pop()!.reject!(err);
    router.go(-1);
  });

  router.afterEach((toRoute: Route) => {
    const stack = parseInt((toRoute.query.m as string) || "0");

    // stack < modal count indicates a "back" navigation from the browser
    // there are some extreme edge-cases (after browser refreshes), but those are practically impossible to tackle...

    if (stack < modalContainer.modals.length) {
      handleBrowserBack(); // no need to await
    }
  });
}

async function handleBrowserBack() {
  const handler = modalContainer.modals[modalContainer.modals.length - 1].dismissHandler;
  if (!handler) {
    modalContainer.modals.pop()!.reject!({ reason: "back" });
    return; // no `router.go` in this case!
  }

  await resetBrowserHistory();

  const cancel = await handler();
  if (!cancel) {
    modalContainer.modals.pop()!.reject!({ reason: "back" });
    router.go(-1);
  }
}

async function resetBrowserHistory() {
  // Note: handler _could_ open a (confirmation) modal as well, that's why we need to make sure the "stack" is kept in sync
  // this triggers another `route.afterEach` which results in a noop.
  router.go(+1);

  // It's a shame `router.go` does not return a `Promise` :-(
  // `await Vue.nextTick()` does not do the trick either...
  // let's hope 50ms is enough for all cases `setTimeout lower or no timeout does not work
  await new Promise(res => setTimeout(res, 50));
}

Vue.use({
  install(/* vue, options = {} */) {
    Vue.prototype.$modal = modal;
  },
});
